/**
 * Zentrale Typdefinitionen des gesamten Systems.
 *
 * Drei Konventionen tragen alles Weitere:
 *
 * 1. ZEIT WIRD ALS "FLOATING WALL CLOCK" GESPEICHERT.
 *    `'2026-09-16T18:00'` - ohne Offset, ohne `Z`. Damit bleibt eine Serie
 *    "jeden Montag 18:00" ueber Sommerzeitwechsel hinweg auf 18:00. Wer hier
 *    UTC-Instants speichert, bekommt stillen Ein-Stunden-Drift.
 *    Nebeneffekt: ISO-Strings sortieren lexikografisch, also unterstuetzt ein
 *    simpler IndexedDB-Index direkt `between()`-Bereichsabfragen.
 *
 * 2. GANZTAEGIGE ZEITRAEUME HABEN EIN EXKLUSIVES ENDE (iCal-Konvention).
 *    Ein Ein-Tages-Termin ist `start: '2026-09-13', end: '2026-09-14'`.
 *    Die Anzeige ist inklusiv; die Umrechnung passiert in genau einem Formatter.
 *
 * 3. GELD IST IMMER EIN INTEGER IN CENT.
 *    Floats fuer Euro-Betraege sind ein Bug, den man ein halbes Jahr spaeter in
 *    einem um zwei Cent falschen Budget findet.
 */

/** UUID, erzeugt mit `crypto.randomUUID()`. */
export type ID = string;

/** `'YYYY-MM-DD'` */
export type LocalDate = string;

/** `'YYYY-MM-DDTHH:mm'` - floating, minutengenau, ohne Offset. */
export type LocalDateTime = string;

/** Echter Zeitpunkt mit Offset - nur fuer Metadaten wie `createdAt`. */
export type ISOInstant = string;

/**
 * Aktuelle Version des Export-/Datenschemas. Bei Aenderung Migration ergaenzen.
 *
 * 2: Verknuepfungsregel aus Roadmap F-11 (Bereich folgt Ziel/Projekt,
 *    `Task.goalId`, keine Verweise ins Leere) - siehe `db/migrations`.
 * 3: Schlaf ins Morgenjournal (V-08) - siehe `domain/journal/sleep.ts`.
 * 4: Journal-Bewertungen 1-10 mit Kategorien (V-09) - `domain/journal/ratings.ts`.
 * 5: Buecher werden Wissensquellen (V-13) - `domain/wissen/index.ts`.
 */
export const CURRENT_SCHEMA_VERSION = 5;

export interface Entity {
  id: ID;
  /** `null` bedeutet bewusst "ohne Bereich", nicht "unbekannt". */
  areaId: ID | null;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

// ---------------------------------------------------------------------------
// Areas - die Querschnittsdimension des gesamten Systems
// ---------------------------------------------------------------------------

/**
 * Ein Lebensbereich. Areas sind nicht kalenderspezifisch: Termine, Aufgaben,
 * Ziele, Gewohnheiten und Ausgaben haengen alle an einer Area, damit sich das
 * ganze Dashboard mit einem Klick auf einen Bereich einschraenken laesst.
 */
/** Feste Bereiche (BE-02): immer vorhanden, nicht loeschbar. */
export type FesterBereich = 'gesundheit' | 'schule' | 'finanzen';

export interface Area {
  id: ID;
  name: string;
  /** Gesetzt bei den drei festen Bereichen - die lassen sich nur archivieren. */
  fest?: FesterBereich;
  /** Kurze Beschreibung unter dem Namen auf der Bereichsseite (D-00c). */
  beschreibung?: string;
  /** OKLCH-String, z. B. `'oklch(0.65 0.15 150)'`. Erlaubt `color-mix()`. */
  color: string;
  /** Name eines lucide-react-Icons. */
  icon?: string;
  order: number;
  archived: boolean;
  /** Ob dieser Bereich spaeter eine Achse im Lebensrad bildet. */
  lifeWheelEnabled: boolean;
  /** Wunschverteilung (F-28): so viele Stunden pro Woche sollen in diesen Bereich gehen. */
  zielStundenWoche?: number;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

/**
 * Die vier Eisenhower-Stufen (Roadmap V-03). Termine tragen sie seit D-03
 * als Punkt oben rechts; Aufgaben leiten sie spaeter aus `important` und
 * `urgent` ab.
 */
export type Priority = 'dringend-wichtig' | 'wichtig' | 'dringend' | 'weder';

/**
 * Dreistufige Prioritaet fuer Projekte (V-04) und spaeter Ziele (V-05) -
 * bewusst nicht Eisenhower: "dringend" aendert sich bei laengeren Vorhaben
 * staendig.
 */
export type ProjectPriority = 'hoch' | 'mittel' | 'niedrig';

/**
 * Ziele nutzen dieselbe Skala (V-05), aber eine eigene, hellere Farbabstufung -
 * deshalb eigene Farbschluessel, damit Ziel und Projekt unterscheidbar bleiben.
 */
export type GoalPriorityKey = `ziel-${ProjectPriority}`;

/** Jede Prioritaetsstufe, fuer die es eine Farbe gibt. */
export type PriorityKey = Priority | ProjectPriority | GoalPriorityKey;

/**
 * Eine selbst gewaehlte Prioritaetsfarbe (V-04, "jederzeit aenderbar").
 * Ueberschreibt das Token `--color-prio-<id>` aus tailwind.css; ohne Zeile
 * gilt der Standard. In der Sicherung enthalten.
 */
export interface PriorityColor {
  id: PriorityKey;
  color: string;
  updatedAt: ISOInstant;
}

/**
 * Eine eigene, benannte Kategorie fuer Termine, z. B. "Pruefung" (D-03).
 * Standardmaessig farbiger Rand des Termins; eine Legende im Kalender zeigt
 * die Bedeutung. Unabhaengig vom Bereich.
 */
export interface Category {
  id: ID;
  name: string;
  /** OKLCH-String wie bei `Area.color`. */
  color: string;
  order: number;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

/**
 * Eine gemerkte Lieblingsfarbe (Zusatz zu D-02): steht in jeder Farbauswahl
 * ganz vorn, dauerhaft - anders als "Zuletzt", das sich staendig aendert.
 * Liegt in der Datenbank und damit in der Sicherung.
 */
export interface SavedColor {
  id: ID;
  /** OKLCH-String, schon lesbar gemacht (`domain/colors/oklch.ts`). */
  color: string;
  order: number;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

/**
 * Eine Markierung eines Termins (D-03): fehlt das Feld, gilt die
 * Standardbelegung; `null` heisst bewusst leer; ein String ist eine eigene
 * Farbe fuer genau diesen Termin.
 */
export type MarkValue = string | null;

// ---------------------------------------------------------------------------
// Kalender
// ---------------------------------------------------------------------------

export type EventStatus = 'confirmed' | 'cancelled';

/**
 * Im Store `events` leben drei Zeilenformen nebeneinander:
 *
 * - **Einzeltermin**  - weder `rrule` noch `recurringEventId`
 * - **Serie (Master)** - `rrule` gesetzt
 * - **Ausnahme (Override)** - `recurringEventId` + `originalStart` gesetzt
 */
export interface CalendarEvent extends Entity {
  title: string;
  notes?: string;
  location?: string;

  allDay: boolean;
  /** Bei `allDay` ein `LocalDate`, sonst ein `LocalDateTime`. */
  start: LocalDateTime | LocalDate;
  /** Bei `allDay` EXKLUSIV (siehe Konvention 2). */
  end: LocalDateTime | LocalDate;
  /** Wird gespeichert, in v1 aber nicht ausgewertet. */
  tz: string;

  /** RFC-5545-Regel ohne `DTSTART`, z. B. `'FREQ=WEEKLY;BYDAY=MO,WE'`. */
  rrule?: string;
  /** Startzeitpunkte gestrichener oder ueberschriebener Vorkommen. */
  exdates?: LocalDateTime[];

  /** Nur bei Ausnahmen: die Serie, zu der diese Zeile gehoert. */
  recurringEventId?: ID;
  /** Nur bei Ausnahmen: welches Vorkommen der Serie sie ersetzt. */
  originalStart?: LocalDateTime;

  /** Gesetzt, wenn der Termin durch Time-Blocking aus einer Aufgabe entstand. */
  sourceTaskId?: ID;
  /** Trainingseinheit aus dem Plan (F-34 E), ein Termin je Vorkommen. */
  training?: TrainingsTermin;
  /** Aus einer Tagesvorlage eingefuegt (F-36). */
  vorlage?: VorlagenHerkunft;
  /** Schulstunde (F-22): Fach des Stundenplans. Die Fachfarbe ist der Standard-Rand. */
  subjectId?: ID;
  /** Gesetzt, wenn der Termin eine Klausur ist (F-22 Stufe 3). */
  klausur?: { themen: KlausurThema[] };
  /** Verknuepftes Projekt (F-04 d). Die Projektseite zeigt den Termin. */
  projectId?: ID;
  /** Verknuepfte Aufgaben (F-04 d), z. B. Vorbereitung fuer ein Treffen. */
  taskIds?: ID[];

  /** Eigene Kategorie (D-03) - Standard fuer den Rand. */
  categoryId?: ID;
  /** Prioritaet (D-03, Stufen aus V-03) - Standard fuer den Punkt. */
  priority?: Priority;
  /** Markierungen: fehlt = Standard (Fuellung Bereich, Rand Kategorie, Punkt Prioritaet). */
  markFill?: MarkValue;
  markBorder?: MarkValue;
  markDot?: MarkValue;

  /**
   * Minuten vor Beginn (F-04 f); ganztaegig vor 8 Uhr des Tages. Erinnert
   * wird, solange die App offen ist - im Fenster und, wenn erlaubt, als
   * Benachrichtigung des Browsers. Ohne Server gibt es keine Push-Nachricht.
   */
  reminderMinutes?: number[];

  status: EventStatus;

  /**
   * Gespiegelter Indexwert fuer `rrule != null`.
   * IndexedDB kann Booleans und `null` nicht zuverlaessig indizieren - deshalb
   * `0 | 1` statt `boolean`. Wird ausschliesslich vom Repository gepflegt.
   */
  isRecurring: 0 | 1;
}

/**
 * Ein einzelnes Vorkommen im sichtbaren Zeitraum.
 *
 * Wird bei jeder Abfrage neu berechnet und NIE persistiert - sonst muesste man
 * unendliche Serien materialisieren.
 */
/** Was aus einem vergangenen Termin wurde (F-29). */
export type TerminStatus = 'stattgefunden' | 'ausgefallen' | 'anders';

/**
 * Bestaetigung eines einzelnen Vorkommens (F-29). Schluessel: bei Serien
 * `Serie::urspruenglicher Start`, sonst die Termin-ID - so ueberlebt sie
 * auch das Verschieben eines Vorkommens. Die Serie selbst bleibt unberuehrt.
 */
export interface TerminBestaetigung {
  id: string;
  /** Serie bzw. Einzeltermin - zum Aufraeumen beim Loeschen. */
  eventId: ID;
  /** Tag des Vorkommens - fuer Abfragen nach Zeitraum. */
  datum: LocalDate;
  status: TerminStatus;
  /** Bei "anders gelaufen": tatsaechlicher Beginn und tatsaechliches Ende. */
  start?: LocalDateTime;
  end?: LocalDateTime;
  /** Optional bei "ausgefallen", z. B. "krank". */
  grund?: string;
  updatedAt: ISOInstant;
}

export interface CalendarOccurrence {
  /** `` `${eventId}::${originalStart}` `` - stabiler Key fuer React und Drag. */
  occurrenceId: string;
  eventId: ID;
  isRecurring: boolean;
  isOverride: boolean;
  /** Bei Serienvorkommen der urspruengliche Start - der Schluessel fuer Ausnahmen. */
  originalStart?: LocalDateTime;
  /** Bei Serienvorkommen (auch Ausnahmen) die ID der Serie (F-29). */
  seriesId?: ID;
  /** Bestaetigung (F-29), beim Laden dazugelesen. */
  bestaetigung?: TerminBestaetigung;
  /** Zeitblock einer Aufgabe: ist sie abgehakt? (F-29, beim Laden dazugelesen) */
  blockErledigt?: boolean;
  /** Trainingstermin: tatsaechlicher Beginn und tatsaechliches Ende aus dem Protokoll (F-34 E). */
  blockZeit?: { start: LocalDateTime; end: LocalDateTime };

  start: LocalDateTime | LocalDate;
  end: LocalDateTime | LocalDate;
  allDay: boolean;

  title: string;
  areaId: ID | null;
  sourceTaskId?: ID;
  training?: TrainingsTermin;
  vorlage?: VorlagenHerkunft;
  /** Schulstunde (F-22). */
  subjectId?: ID;
  /** Verknuepfungen (F-04 d). */
  projectId?: ID;
  taskIds?: ID[];
  /** Erinnerungen (F-04 f). */
  reminderMinutes?: number[];
  location?: string;
  categoryId?: ID;
  priority?: Priority;
  markFill?: MarkValue;
  markBorder?: MarkValue;
  markDot?: MarkValue;
}

/**
 * Verknuepfung eines Kalendertermins mit einer Trainingseinheit (F-34 E).
 * Jede eingefuegte Einheit ist ein eigener Termin - Verschieben betrifft so
 * nur dieses eine Vorkommen.
 */
export interface TrainingsTermin {
  planEinheitId: ID;
  /** Planversion, aus der der Termin stammt - fuer „Plan geaendert“. */
  planId: ID;
  /** Der Plantag, fuer den die Einheit gilt (auch wenn der Termin verschoben wurde). */
  datum: LocalDate;
  /** Gemeinsame Kennung eines Einfuegevorgangs - fuer „Rueckgaengig“. */
  einfuegung: ID;
}

/** Herkunft eines Termins aus einer Tagesvorlage (F-36) - fuer „Rueckgaengig“ und die Tagesbeschriftung. */
export interface VorlagenHerkunft {
  vorlageId: ID;
  /** Name zum Zeitpunkt des Einfuegens - spaetere Aenderungen der Vorlage aendern den Tag nicht. */
  name: string;
  datum: LocalDate;
  /** Gemeinsame Kennung einer Anwendung. */
  einfuegung: ID;
}

/** Welche Vorkommen einer Serie eine Bearbeitung betrifft. */
export type RecurrenceEditScope = 'dieser' | 'dieser_und_folgende' | 'alle';

// ---------------------------------------------------------------------------
// Aufgaben und Projekte
// ---------------------------------------------------------------------------

export type TaskStatus =
  | 'inbox'
  | 'naechste'
  | 'wartet'
  | 'geplant'
  | 'erledigt'
  | 'irgendwann';

export interface Task extends Entity {
  title: string;
  notes?: string;
  status: TaskStatus;
  projectId?: ID;
  /**
   * Direkt an einem Ziel, fuer kleine Ziele ohne eigenes Projekt (F-11).
   * Nie gleichzeitig mit `projectId` - siehe `domain/links/hierarchy.ts`.
   */
  goalId?: ID;
  /** Fach einer Hausaufgabe (F-22) - Hausaufgaben sind normale Aufgaben. */
  subjectId?: ID;
  /** Lernplan-Eintrag einer Klausur (F-22 Stufe 3): ID ihres Termins. */
  examId?: ID;
  columnId?: ID;

  /** Eisenhower-Achsen. */
  important: boolean;
  urgent: boolean;

  /** Deadline: bis wann fertig (F-09). Nicht dasselbe wie `plannedDate`. */
  dueDate?: LocalDate;
  /** Optionale Uhrzeit der Deadline, `'HH:mm'`. Ohne gilt das Ende des Tages. */
  dueTime?: string;
  /** Taucht im Tagesplan dieses Datums auf. */
  plannedDate?: LocalDate;
  /** Maximal drei pro `plannedDate`; die Grenze setzt die UI durch. */
  isBigThree?: boolean;
  estimateMinutes?: number;
  /** Gegenstueck zu `CalendarEvent.sourceTaskId`. */
  timeBlockEventId?: ID;

  order: number;
  completedAt?: ISOInstant;
  /** GTD-Kontexte, z. B. `['@zuhause', '@rechner']`. multiEntry-Index. */
  contexts?: string[];
  /**
   * Wiederkehrende Aufgabe (F-24): dieselbe Regel wie bei Terminen. Die
   * Aufgabe steht immer fuer ihr gerade faelliges Vorkommen - Deadline und
   * Plantag gehoeren zu `vorkommen` und wandern beim Abhaken mit.
   */
  wiederholung?: AufgabenWiederholung;
  /** Erledigtes oder einzeln geaendertes Vorkommen einer Serie (F-24). */
  serie?: { id: ID; vorkommen: LocalDate };
}

export interface AufgabenWiederholung {
  /** RFC-5545 ohne DTSTART, z. B. `'FREQ=WEEKLY;BYDAY=SA'`. */
  rrule: string;
  /** Erster Tag der Serie (DTSTART). */
  start: LocalDate;
  /** Das Vorkommen, fuer das die Aufgabe gerade steht. */
  vorkommen: LocalDate;
}

export type ProjectStatus = 'aktiv' | 'pausiert' | 'abgeschlossen' | 'irgendwann';

/** Ein Meilenstein eines Projekts (F-10). */
export interface Milestone {
  id: ID;
  title: string;
  date: LocalDate;
  done: boolean;
}

export interface Project extends Entity {
  title: string;
  description?: string;
  status: ProjectStatus;
  /** V-04. Fehlt sie (aeltere Daten), gilt "mittel" - siehe `projectPriority`. */
  priority?: ProjectPriority;
  /** Freie Notizen mit einfacher Formatierung (F-10, wie F-05). */
  notes?: string;
  /** Zwischenschritte mit eigenem Datum (F-10) - leben im Projekt wie Key Results im Ziel. */
  milestones?: Milestone[];
  goalId?: ID;
  /** Deadline (F-09), optional mit Uhrzeit `'HH:mm'`. */
  deadline?: LocalDate;
  deadlineTime?: string;
  columns: { id: ID; name: string; order: number }[];
}

// ---------------------------------------------------------------------------
// Ziele (Phase 2) - jetzt schon definiert, damit spaeter keine Migration noetig ist
// ---------------------------------------------------------------------------

export type GoalHorizon = 'jahr' | 'quartal' | '12wochen';
export type GoalStatus = 'offen' | 'laeuft' | 'erreicht' | 'verworfen';

/**
 * Art eines Key Results (V-06). Alle Arten lassen sich auf Start, Ist und
 * Ziel abbilden - so bleibt EINE Fortschrittsformel, und alte Key Results
 * (ohne `type`) gelten unveraendert als "zahl".
 */
export type KeyResultType = 'zahl' | 'janein' | 'prozent' | 'anzahl';
export type KeyResultDirection = 'steigern' | 'senken';

/** Ein Messpunkt im Verlauf (Vorschlag zu V-06, fuer Diagramme in F-02). */
export interface KeyResultMeasurement {
  date: LocalDate;
  value: number;
}

export interface KeyResult {
  id: ID;
  title: string;
  /** Fehlt bei aelteren Daten - dann "zahl". */
  type?: KeyResultType;
  /** Nur bei "zahl": steigern oder senken. Fehlt: aus Start und Ziel abgeleitet. */
  direction?: KeyResultDirection;
  /** Freie Einheit: `'kg'`, `'EUR'`, `'Seiten'`, `'%'`. */
  unit: string;
  start: number;
  target: number;
  current: number;
  /** Verlauf des Ist-Werts, hoechstens ein Eintrag je Tag. */
  history?: KeyResultMeasurement[];
  /** Optionale eigene Deadline (F-09). */
  deadline?: LocalDate;
  deadlineTime?: string;
  /**
   * Automatisch befuellt (V-06, F-22): der Ist-Wert kommt dann aus einer
   * Gewohnheit bzw. dem Notenschnitt eines Fachs statt von Hand.
   */
  quelle?: KeyResultQuelle;
}

/** Woher ein Key Result seinen Ist-Wert automatisch bekommt. */
export type KeyResultQuelle =
  /** Erledigte Tage einer Gewohnheit - im Zeitraum des Ziels oder in der laufenden Woche. */
  | { art: 'gewohnheit'; habitId: ID; zeitraum: 'ziel' | 'woche' }
  /** Gewichteter Notenschnitt eines Fachs, im eingestellten Notensystem. */
  | { art: 'note'; subjectId: ID }
  /** Kennwert aus dem Trainingsprotokoll (F-34 D), z. B. bester 10-km-Lauf. */
  | { art: 'training'; mass: TrainingsMass }
  /** Kennwert aus der Ernaehrung (F-35 E), z. B. Tage mit erreichtem Eiweissziel. */
  | { art: 'ernaehrung'; mass: ErnaehrungsMass };

/** Kriterien des Ernaehrungs-Scores (F-35 D) - Bedeutung in domain/ernaehrung/analyse.ts. */
export type ErnaehrungsKriterium = 'eiweissZiel' | 'trinkZiel' | 'kcalZiel' | 'ballastMin' | 'zuckerMax' | 'salzMax' | 'gemueseObstMin' | 'suessFastfoodMax';

/**
 * Kennwerte aus der Ernaehrung (F-35 E) - im Zeitraum des Ziels oder in der
 * laufenden Woche. Kriterien wie beim Ernaehrungs-Score (mit dessen Grenzen).
 */
export type ErnaehrungsMass =
  /** Tage, an denen das Kriterium erfuellt war. */
  | { art: 'tage_kriterium'; kriterium: ErnaehrungsKriterium; zeitraum: 'ziel' | 'woche' }
  /** Durchschnittlicher Score der vollstaendigen Tage (1-10). */
  | { art: 'score_schnitt'; zeitraum: 'ziel' | 'woche' }
  /** Vollstaendig erfasste Tage. */
  | { art: 'tage_vollstaendig'; zeitraum: 'ziel' | 'woche' };

/**
 * Kennwerte aus dem Trainingsprotokoll (F-34 D). Bestwerte zaehlen im
 * Zeitraum des Ziels, Wochenwerte in der laufenden Woche.
 */
export type TrainingsMass =
  /** Beste Zeit (Minuten) fuer `km` - aus Laeufen ab dieser Distanz, aufs Tempo umgerechnet. */
  | { art: 'lauf_zeit'; km: number }
  /** Meiste Wiederholungen in einem Satz. */
  | { art: 'wdh_max'; uebungId: ID }
  /** Bestes geschaetztes Maximalgewicht (Epley). */
  | { art: 'e1rm'; uebungId: ID }
  /** Gelaufene Kilometer der laufenden Woche. */
  | { art: 'km_woche' }
  /** Fertige Einheiten der laufenden Woche. */
  | { art: 'einheiten_woche' }
  /** Hoechste erreichte Skill-Stufe (1 = erste). */
  | { art: 'skill_stufe'; uebungId: ID };

export interface Goal extends Entity {
  /** V-05, gleiche Skala wie Projekte. Fehlt sie (aeltere Daten), gilt "mittel". */
  priority?: ProjectPriority;
  title: string;
  why?: string;
  horizon: GoalHorizon;
  /**
   * Uebergeordnetes Ziel, genau eine Ebene tief: ein Jahresziel traegt
   * Quartals- oder 12-Wochen-Ziele. Tiefere Baeume laden zum Verzetteln ein.
   */
  parentGoalId?: ID;
  periodStart: LocalDate;
  periodEnd: LocalDate;
  /**
   * Deadline (F-09), optional mit Uhrzeit. Unabhaengig vom Zeitraum: der
   * beschreibt den Zyklus (Jahr, Quartal), die Deadline eine harte Frist.
   */
  deadline?: LocalDate;
  deadlineTime?: string;
  keyResults: KeyResult[];
  status: GoalStatus;
  /** Freie Notizen der Detailseite (F-12), mit einfacher Formatierung (F-05). */
  notes?: string;
}

export interface LifeWheelSnapshot {
  id: ID;
  date: LocalDate;
  /** `areaId` -> Bewertung 1..10 */
  scores: Record<ID, number>;
  note?: string;
}

export interface WeeklyReview {
  id: ID;
  /** ISO-Wochenkennung, z. B. `'2026-W38'`. */
  isoWeek: string;
  answers: Record<string, string>;
  /** Wortlaut der Fragen beim Beantworten (F-27), je Antwortschluessel. */
  wortlaut?: Record<string, string>;
  /** Bewertungen 1-10 je Kategorie (V-11). */
  ratings?: Record<ID, number>;
  /** Werte typisierter Felder (F-32): Zahl, Ja/Nein, Uhrzeit, Checkliste. */
  werte?: Record<string, FeldWert>;
  completedAt?: ISOInstant;
}

/** Monatsreflexion (F-14) - gleich aufgebaut wie die Woche. */
export interface MonthlyReview {
  /** `'2026-09'` */
  id: string;
  month: string;
  answers: Record<string, string>;
  /** Wortlaut der Fragen beim Beantworten (F-27), je Antwortschluessel. */
  wortlaut?: Record<string, string>;
  ratings?: Record<ID, number>;
  werte?: Record<string, FeldWert>;
  completedAt?: ISOInstant;
}

// ---------------------------------------------------------------------------
// Gewohnheiten (Phase 1.5)
// ---------------------------------------------------------------------------

export type HabitCadence =
  | { type: 'taeglich' }
  /** `days`: 0 = Sonntag ... 6 = Samstag (wie `Date.getDay()`). */
  | { type: 'wochentage'; days: number[] }
  | { type: 'x_pro_woche'; count: number };

export interface Habit extends Entity {
  name: string;
  cadence: HabitCadence;
  /** Ohne `targetValue` ist die Gewohnheit ein reines Ja/Nein. */
  targetValue?: number;
  unit?: string;
  routine?: 'morgen' | 'abend' | null;
  order: number;
  archived: boolean;
}

export interface HabitEntry {
  /** Deterministisch `` `${habitId}:${date}` `` - macht Upserts trivial. */
  id: ID;
  habitId: ID;
  date: LocalDate;
  done: boolean;
  value?: number;
  note?: string;
  updatedAt: ISOInstant;
}

// ---------------------------------------------------------------------------
// Gesundheit und Journal (Phase 2)
// ---------------------------------------------------------------------------

export type Scale5 = 1 | 2 | 3 | 4 | 5;

export type BewertungsOrt = 'morgen' | 'abend' | 'woche' | 'monat';

/**
 * Eine Bewertungskategorie des Journals (V-09), z. B. "Energie" am Morgen.
 * Skala 1-10, 10 = gut (Leitlinie 6) - deshalb "Gelassenheit" statt "Stress".
 */
export interface JournalCategory {
  id: ID;
  name: string;
  /** Wo bewertet wird: Morgen- oder Abendjournal, Wochen- oder Monatsreflexion (V-11). */
  journal: BewertungsOrt;
  /** Kurzer Hinweis zur Skala, z. B. "10 = entspannt, 1 = sehr gestresst". */
  hint?: string;
  order: number;
  /** Ausgeblendet statt geloescht - alte Werte bleiben erhalten. */
  hidden: boolean;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

export interface JournalEntry extends Entity {
  date: LocalDate;
  type: 'morgen' | 'abend' | 'frei';
  /** Bewertungen 1-10 je Kategorie (V-09). */
  ratings?: Record<ID, number>;
  /**
   * Die urspruenglichen 1-5-Werte vor der Umstellung auf 1-10 - bleiben als
   * Sicherung erhalten und markieren umgerechnete (groebere) Werte.
   */
  legacy5?: { mood?: number; energy?: number; sleepQuality?: number };
  /** @deprecated Vor V-09 (1-5); wird zu `ratings` umgerechnet. */
  mood?: Scale5;
  /** @deprecated Vor V-09 (1-5); wird zu `ratings` umgerechnet. */
  energy?: Scale5;
  gratitude?: string[];
  body?: string;
  /**
   * Schlaf der vergangenen Nacht - seit V-08 nur im Morgenjournal. Die Dauer
   * wird aus Einschlaf- und Aufwachzeit berechnet, wenn beide da sind.
   */
  sleepHours?: number;
  /** @deprecated Vor V-09 (1-5); seitdem die Kategorie "Schlafqualitaet". */
  sleepQuality?: Scale5;
  /** 'HH:mm' */
  bedtime?: string;
  /** 'HH:mm' */
  wakeTime?: string;
  /** Reflexion im Abendjournal (V-10) - keine Frage ist Pflicht. */
  reflexion?: Reflexion;
}

/** Status eines gemerkten Gedankens (F-13, Vorschlag). */
export type GedankenStatus = 'neu' | 'spaeter' | 'arbeit' | 'erledigt';

/**
 * Ein Gedanke aus dem Braindump (F-13) - ein einzelner Eintrag, damit sich
 * jeder fuer sich merken laesst. Gehoert zum Journaltag `date`.
 */
/**
 * Herkunft eines Gedankens: Wochen-/Monatsreflexion (F-15, Schluessel
 * `'2026-W39'` bzw. `'2026-09'`) oder Wissensquelle (F-20, Schluessel =
 * ID, dazu der Titel fuer den Rueckverweis).
 */
export interface GedankenQuelle {
  art: 'woche' | 'monat' | 'wissen';
  key: string;
  titel?: string;
}

export interface Thought {
  id: ID;
  /** Journaltag bzw. bei Gedanken aus einer Reflexion der Tag, an dem er notiert wurde. */
  date: LocalDate;
  /** Fehlt bei Gedanken aus dem Journal. */
  quelle?: GedankenQuelle;
  text: string;
  /** "Fuer spaeter merken" - `0 | 1`, weil indiziert. */
  gemerkt: 0 | 1;
  status: GedankenStatus;
  /** Weitere Gedanken dazu, wenn man spaeter laenger nachdenkt. */
  note?: string;
  /** Wozu er weiterverarbeitet wurde (Aufgabe, Projekt, Ziel). */
  umgewandelt?: { art: 'aufgabe' | 'projekt' | 'ziel'; id: ID };
  /**
   * Zuordnung (F-17) nach der Regel aus F-11: Projekt ODER Ziel, der Bereich
   * folgt dem Elternteil. Ohne alles ist der Gedanke "unzugeordnet".
   */
  areaId?: ID | null;
  projectId?: ID;
  goalId?: ID;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

export interface Reflexion {
  gut?: string;
  schlecht?: string;
  /** Was habe ich gelernt? (Rueckblick, Einsicht) */
  erkenntnis?: string;
  /** Was aendere ich dauerhaft? */
  optimierung?: string;
  /** Was mache ich morgen konkret anders? */
  morgen?: string;
  /** Aufgabe, zu der der Vorsatz per Klick wurde. */
  morgenAufgabeId?: ID;
  /** Antworten auf eigene Fragen (F-27), je Frage-ID. */
  eigene?: Record<ID, string>;
  /**
   * Antworten auf Felder, die kein Freitext sind (F-32), je Feld-ID: Zahl,
   * Ja/Nein, Uhrzeit (`HH:mm`) oder die abgehakten Punkte einer Checkliste
   * (als Text - so bleiben sie lesbar, auch wenn ein Punkt spaeter umbenannt wird).
   */
  werte?: Record<ID, FeldWert>;
  /**
   * Wortlaut der Frage beim Beantworten (F-27), je Frage-ID bzw.
   * Standard-Schluessel - so zeigt ein alter Eintrag die Frage, wie sie
   * damals hiess, auch wenn sie spaeter umbenannt wurde.
   */
  wortlaut?: Record<string, string>;
}

/**
 * Eine Frage der Abendreflexion (F-27). Die fuenf Standardfragen (V-10)
 * haben ihren Schluessel als ID (`gut`, `schlecht`, ...) und lassen sich nur
 * ausblenden; eigene Fragen bekommen eine neue ID. Geloescht wird nie -
 * sonst fehlte alten Antworten ihre Frage.
 */
/** Feldtyp (F-32). Bewertungen 1-10 sind Journal-Kategorien (V-09) und kein Feldtyp hier. */
export type FeldTyp = 'text' | 'langtext' | 'janein' | 'zahl' | 'uhrzeit' | 'checkliste';
export type FeldWert = number | boolean | string | string[];

export interface ReflexionsFrageDef {
  id: ID;
  /** Wo die Frage gestellt wird; fehlt bei Fragen von vor der Erweiterung = Abendjournal. */
  ort?: 'morgen' | 'abend' | 'frei' | 'woche' | 'monat';
  /** Feldtyp (F-32); fehlt = langer Freitext wie bisher. */
  typ?: FeldTyp;
  /** Einheit bei Zahlen, z. B. „l“ oder „h“. */
  einheit?: string;
  /** Punkte einer Checkliste. */
  punkte?: string[];
  /** Abschnitt, in dem das Feld steht (F-32); fehlt = im Block „Fragen“. */
  abschnittId?: ID;
  /** Schluessel der Antwort, wenn er von der ID abweicht (Standardfragen der Reflexionen). */
  schluessel?: string;
  frage: string;
  hint?: string;
  order: number;
  hidden: boolean;
  standard: boolean;
  /** Antwort erscheint am naechsten Morgen oben im Morgenjournal. */
  mitnahme: boolean;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

/**
 * Aufbau eines Journals bzw. einer Reflexion (F-32): Reihenfolge der
 * eingebauten Bloecke und eigenen Abschnitte, jeweils ausblendbar. Fehlt der
 * Datensatz, gilt der Standardaufbau.
 */
export interface JournalLayout {
  id: 'morgen' | 'abend' | 'frei' | 'woche' | 'monat';
  eintraege: LayoutEintrag[];
  updatedAt: ISOInstant;
}

export type LayoutEintrag =
  | { art: 'block'; key: string; hidden?: boolean }
  | { art: 'abschnitt'; id: ID; titel: string; hidden?: boolean };

// ---------------------------------------------------------------------------
// Ressourcen (Phase 3)
// ---------------------------------------------------------------------------

export interface Account extends Entity {
  name: string;
  kind: 'giro' | 'bar' | 'sparen';
  /** In Cent (siehe Konvention 3). */
  openingBalanceCents: number;
}

export interface Envelope extends Entity {
  name: string;
  monthlyBudgetCents: number;
  rollover: boolean;
}

export interface Transaction extends Entity {
  date: LocalDate;
  /** In Cent. Negativ bei Ausgaben. */
  amountCents: number;
  accountId: ID;
  envelopeId?: ID;
  payee?: string;
  note?: string;
  kind: 'ausgabe' | 'einnahme' | 'umbuchung';
  subscriptionId?: ID;
}

export interface Subscription extends Entity {
  name: string;
  amountCents: number;
  cycle: 'monatlich' | 'quartal' | 'jaehrlich';
  nextDue: LocalDate;
  cancelBy?: LocalDate;
  noticePeriodDays?: number;
  /** B-02: faellige Zahlungen automatisch als Buchung eintragen. */
  autoBuchen?: boolean;
  /** B-02: Einnahme (z. B. Taschengeld) statt Ausgabe; ohne Angabe Ausgabe. */
  kind?: 'ausgabe' | 'einnahme';
  envelopeId?: ID;
}

/** Art einer Wissensquelle (V-13). */
export type WissensTyp =
  | 'buch' | 'hoerbuch' | 'video' | 'podcast' | 'artikel' | 'studie' | 'kurs' | 'doku' | 'sonstiges';

/**
 * Eine Wissensquelle (V-13) - frueher nur Buecher, daher Tabelle und Typ
 * weiter `books`/`Book`. Die Statuswerte bleiben, ihre Namen sind neu:
 * Will ich aufnehmen / In Bearbeitung / Abgeschlossen / Abgebrochen.
 *
 * Bereiche stehen in `areaIds` (mehrere moeglich); `areaId` bleibt `null`.
 */
export interface Book extends Entity {
  title: string;
  typ: WissensTyp;
  /** Urheber oder Quelle: Autor, Kanal, Zeitschrift ... */
  author?: string;
  url?: string;
  status: 'wunsch' | 'lese' | 'gelesen' | 'abgebrochen';
  tags: string[];
  priority?: ProjectPriority;
  /** Einheit je Typ (Seiten, Minuten, Lektionen ...), siehe `domain/wissen`. */
  fortschritt?: { aktuell: number; gesamt: number };
  startedAt?: LocalDate;
  finishedAt?: LocalDate;
  /** 1-10 (Leitlinie 6): wie wertvoll war es fuer mich? */
  rating?: number;
  /** Urspruenglicher Wert der alten 1-5-Skala, falls umgerechnet. */
  ratingLegacy5?: Scale5;
  areaIds: ID[];
  /** Verknuepfte Projekte und Ziele (F-19) - beliebig viele, keine Hierarchie. */
  projectIds?: ID[];
  goalIds?: ID[];
  /** Freie Mitschrift beim Lesen, Schauen, Hoeren (F-20). */
  notes?: string;
  /** Die Kernaussagen, die ich mitnehmen will (F-20). */
  erkenntnisse?: string;
  /** Zitate und Stellen mit Seite oder Zeitstempel (F-20). */
  stellen?: WissensStelle[];
  /** Erkenntnisse, die schon zur Aufgabe oder zum Gedanken wurden (F-20). */
  umgesetzt?: { text: string; art: 'aufgabe' | 'gedanke'; id: ID }[];
}

export interface WissensStelle {
  id: ID;
  /** `'S. 84'`, `'12:30'` - frei, damit es fuer jeden Typ passt. */
  ort: string;
  text: string;
}

export type BookStatus = Book['status'];

export interface Contact extends Entity {
  name: string;
  birthday?: LocalDate;
  /** Erinnerung, wenn der letzte Kontakt laenger als so viele Tage her ist. */
  contactEveryDays?: number;
  lastContact?: LocalDate;
  notes?: string;
}

// ---------------------------------------------------------------------------
// Ressourcen & Wissen
// ---------------------------------------------------------------------------

/**
 * Notiz und Lesezeichen sind DASSELBE mit einem Feld Unterschied: ein
 * Lesezeichen ist eine Notiz mit `url`. Zwei getrennte Typen haetten zwei
 * Listen, zwei Suchen und zwei Formulare bedeutet, und die Frage "ist das
 * jetzt eine Notiz oder ein Lesezeichen?" waere beim Erfassen im Weg.
 */
export type ResourceKind = 'notiz' | 'link';

/**
 * PARA nach Tiago Forte, plus ein Eingangsfach.
 *
 * `inbox` ist bewusst KEINE PARA-Kategorie, sondern das Gegenteil: der Ort,
 * an dem etwas liegt, BEVOR entschieden wurde. Ohne dieses Fach muesste man
 * beim Aufschreiben schon einsortieren - und genau das verhindert, dass man
 * spontane Ideen ueberhaupt aufschreibt.
 *
 * `projekt` bleibt vorerst ohne Verknuepfung: Projekte gibt es als Modul noch
 * nicht. Das Fach existiert trotzdem, damit die Einordnung vollstaendig ist
 * und spaeter nur ein `projectId` dazukommt statt einer Datenmigration.
 */
export type ParaBucket = 'inbox' | 'projekt' | 'bereich' | 'ressource' | 'archiv';

export interface Resource extends Entity {
  kind: ResourceKind;
  title: string;
  /** Freitext. Bei einem Lesezeichen die Notiz dazu, warum es aufgehoben wird. */
  body?: string;
  /** Nur bei `kind: 'link'`. */
  url?: string;
  tags: string[];
  bucket: ParaBucket;
  /** Nur bei Lesezeichen sinnvoll. Nicht indiziert - IndexedDB mag keine Booleans. */
  read?: boolean;
  /** `0 | 1` statt Boolean, weil dieses Feld INDIZIERT ist (siehe db/schema.ts). */
  pinned: 0 | 1;
  /** Schulfach (F-22 Stufe 6) - Mitschriften und Materialien sind Ressourcen mit Fach. */
  subjectId?: ID;
  schulArt?: 'mitschrift' | 'material';
  /** Zuordnung (F-17) wie bei Aufgaben: Projekt ODER Ziel, `areaId` folgt. */
  projectId?: ID;
  goalId?: ID;
  /** Wozu die Notiz weiterverarbeitet wurde (F-17, wie bei Gedanken). */
  umgewandelt?: { art: 'aufgabe' | 'projekt' | 'ziel'; id: ID };
}

/**
 * Ein Dateianhang.
 *
 * Die Bytes liegen in einer EIGENEN Tabelle, nicht in der Ressource. Sonst
 * zoege jede Listenabfrage den gesamten Dateiinhalt mit in den Speicher, nur
 * um einen Titel anzuzeigen.
 *
 * `bytes` ist ein `Uint8Array` und ausdruecklich KEIN `Blob`. Ein Blob waere
 * das naheliegende Mittel, ueberlebt aber `structuredClone` nicht ueberall -
 * in der Testumgebung kommt aus IndexedDB ein leeres Objekt zurueck, die Datei
 * waere also weg. Rohe Bytes ueberstehen die Speicherung nachweislich
 * unbeschaedigt, brauchen keinen Base64-Aufschlag in der Datenbank und lassen
 * sich fuer Vorschau und Download jederzeit mit `new Blob([bytes])` zurueck
 * verwandeln.
 *
 * Anhaenge wandern in die Sicherung mit - dort dann als Base64, was rund ein
 * Drittel Aufschlag kostet. Der Alternative, sie wegzulassen, steht das
 * Kernversprechen der App entgegen: eine Datei, die alles enthaelt. Deshalb
 * die Grenzen in `domain/resources/attachments.ts`.
 */
export interface Attachment {
  id: ID;
  resourceId: ID;
  name: string;
  mimeType: string;
  /** Groesse in Bytes - gespeichert, damit Listen nicht jede Datei anfassen muessen. */
  size: number;
  bytes: Uint8Array<ArrayBuffer>;
  createdAt: ISOInstant;
}

// ---------------------------------------------------------------------------
// Meta
// ---------------------------------------------------------------------------

/** Key-Value-Store fuer Einstellungen, Schema-Version und Caches. */
export interface MetaRecord {
  key: string;
  value: unknown;
}

// ---------------------------------------------------------------------------
// Schule (Roadmap F-22)
// ---------------------------------------------------------------------------

/** Ein Thema einer Klausur - abhakbar, sobald es sitzt. */
export interface KlausurThema {
  id: ID;
  text: string;
  done: boolean;
}

export type LehrplanStatus = 'offen' | 'dabei' | 'sitzt';

/** Ein Thema des Lehrplans (F-22 Stufe 5). */
export interface LehrplanThema {
  id: ID;
  text: string;
  status: LehrplanStatus;
}

/** Ein Schulfach. Farbe aus der erweiterten Palette (D-02), als OKLCH. */
export interface Subject {
  id: ID;
  name: string;
  color: string;
  order: number;
  archived: boolean;
  /** Lehrplan als Board (Stufe 5) - lebt am Fach wie Meilensteine am Projekt. */
  lehrplan?: LehrplanThema[];
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

export type Notensystem = 'punkte' | 'noten';

/**
 * Eine Lernzeit (F-22 Stufe 7). Laeuft die Stoppuhr noch, fehlt `end` - so
 * uebersteht sie Neuladen und Tabwechsel.
 */
export interface StudySession {
  id: ID;
  subjectId: ID;
  start: ISOInstant;
  end?: ISOInstant;
  createdAt: ISOInstant;
}

/** Eine Note (F-22 Stufe 4) - im Notensystem gespeichert, in dem sie eingetragen wurde. */
export interface Grade {
  id: ID;
  subjectId: ID;
  date: LocalDate;
  value: number;
  system: Notensystem;
  /** Gewicht im Durchschnitt, z. B. 2 fuer eine Klausur. */
  weight: number;
  /** Art, z. B. "Klausur", "Mündlich", "Test". */
  art: string;
  note?: string;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

/** Einstellungen der Schulseite - genau eine Zeile mit `id: 'schule'`. */
export interface SchoolSettings {
  id: 'schule';
  /** Punkte 0-15 oder Noten 1-6. */
  notensystem: Notensystem;
  /** Stundenplan im Wechsel von A- und B-Wochen. */
  abWochen: boolean;
  /** Montag einer A-Woche - daraus ergibt sich jede weitere Woche. */
  aWocheAb: LocalDate;
  /** Bereich, in dem Hausaufgaben landen. */
  areaId: ID | null;
  /** Ferien (F-22): in diesen Zeitraeumen fallen die Schulstunden aus. */
  ferien?: Ferien[];
  /** Fuer die Feiertage (Kalender, freie Tage bei Benachrichtigungen, F-41) - fehlt = Brandenburg. */
  bundesland?: Bundesland;
  updatedAt: ISOInstant;
}

/** Bundeslaender, soweit sie eigene Feiertage haben (`lib/holidays/de.ts`). */
export type Bundesland =
  | 'BW' | 'BY' | 'BE' | 'BB' | 'HB' | 'HH' | 'HE' | 'MV' | 'NI'
  | 'NW' | 'RP' | 'SL' | 'SN' | 'ST' | 'SH' | 'TH';

export interface Ferien {
  id: ID;
  name: string;
  /** Erster und letzter Ferientag, beide einschliesslich. */
  von: LocalDate;
  bis: LocalDate;
}
